import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Checkbox, CheckboxGroup, message } from "@/components/ui";
import { ControlOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { api } from "@/lib/apiClient";
import DrawWinnersCard from "./DrawWinnersCard";
import AddAttendeeModal from "./AddAttendeeModal";

type EligibilityRule = {
  id: string;
  name: string;
  scanPointId: string;
  checked: boolean;
};

type Participant = {
  id: string;
  name: string;
  email: string;
  isWinner: boolean;
  registrantId?: string;
};

type Category = {
  id: string;
  name: string;
  prizeName: string;
  winnerCount: number;
  participants: Participant[];
};

type Attendee = {
  id: string;
  name: string;
  email: string;
};

type FilterFieldOption = {
  id: number;
  label: string;
  value: string;
};

type FilterFieldGroup = {
  fieldId: number;
  label: string;
  identifier: string;
  options: FilterFieldOption[];
};

type FilterOptions = {
  fields: FilterFieldGroup[];
};

type Winner = {
  id: string;
  name: string;
  email: string;
  prize: string;
  category: string;
  categoryId?: string;
  registrantId?: string;
};

type Props = {
  eligibilityRules: EligibilityRule[];
  onEligibilityRulesChange: Dispatch<SetStateAction<EligibilityRule[]>>;
  filterOptions: FilterOptions;
  fieldSelections: Record<string, string[]>;
  onFieldSelectionsChange: Dispatch<SetStateAction<Record<string, string[]>>>;
  categories: Category[];
  onCategoriesChange: Dispatch<SetStateAction<Category[]>>;
  attendees: Attendee[];
  onAttendeesChange: Dispatch<SetStateAction<Attendee[]>>;
  winners: Winner[];
  onWinnersChange: Dispatch<SetStateAction<Winner[]>>;
  eligibleCount: number;
  onEligibleCountChange: (count: number) => void;
  eventSlug: string;
};

const PreLuckyDrawSettings = ({
  eligibilityRules,
  onEligibilityRulesChange,
  filterOptions,
  fieldSelections,
  onFieldSelectionsChange,
  categories,
  onCategoriesChange,
  attendees,
  onAttendeesChange,
  winners,
  onWinnersChange,
  eligibleCount,
  onEligibleCountChange,
  eventSlug,
}: Props) => {
  const [addAttendeeModalOpen, setAddAttendeeModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [savingRuleId, setSavingRuleId] = useState<string | null>(null);
  const [savingFilters, setSavingFilters] = useState(false);
  const [categoryBusyId, setCategoryBusyId] = useState<string | null>(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [drawingCategoryId, setDrawingCategoryId] = useState<string | null>(null);

  const showSettingsSaved = () => {
    message.open({
      type: "success",
      content: "Settings saved",
      key: "lucky-draw-settings-saved",
      duration: 2,
    });
  };

  const allRulesUnchecked = useMemo(
    () => eligibilityRules.every((rule) => !rule.checked),
    [eligibilityRules]
  );

  const hasFieldFilters = useMemo(
    () => Object.values(fieldSelections).some((values) => values.length > 0),
    [fieldSelections]
  );

  const noFiltersApplied = allRulesUnchecked && !hasFieldFilters;

  const mergeWinners = (current: Winner[], incoming: Winner[]) => {
    const map = new Map(current.map((w) => [w.id, w]));
    incoming.forEach((w) => map.set(w.id, w));
    return Array.from(map.values());
  };

  const getExcludedAttendeeIds = (): string[] => {
    return categories.flatMap((cat) =>
      cat.participants.map((p) => p.registrantId || p.id)
    );
  };

  const formatWinnersFromCategory = (category: Category, winnerPayloads: Participant[]) =>
    winnerPayloads.map((winner) => ({
      id: winner.id,
      name: winner.name,
      email: winner.email,
      prize: (winner as any).prize || category.prizeName,
      category: (winner as any).category || category.name,
      categoryId: category.id,
      registrantId: (winner as any).registrantId,
    }));

  const applyEligibilityResponse = (data: any) => {
    if (data.eligibilityRules) {
      onEligibilityRulesChange(data.eligibilityRules);
    }
    if (data.attendees) {
      onAttendeesChange(data.attendees);
    }
    if (data.fieldSelections) {
      onFieldSelectionsChange(data.fieldSelections);
    }
    if (typeof data.eligibleRegistrantCount === "number") {
      onEligibleCountChange(data.eligibleRegistrantCount);
    } else if (data.attendees) {
      onEligibleCountChange(data.attendees.length);
    }
  };

  const handleRuleChange = async (ruleId: string, checked: boolean) => {
    const previousRules = eligibilityRules;
    onEligibilityRulesChange(
      previousRules.map((rule) => (rule.id === ruleId ? { ...rule, checked } : rule))
    );
    setSavingRuleId(ruleId);

    try {
      const data = await api.patch<any>(`/events/${eventSlug}/lucky_draw/eligibility_rules`, {
        ruleId,
        checked,
      });

      applyEligibilityResponse(data);
      message.success("Eligibility rules updated");
    } catch (error) {
      console.error("Error updating eligibility rules:", error);
      message.error("Failed to update eligibility rules");
      onEligibilityRulesChange(previousRules);
    } finally {
      setSavingRuleId(null);
    }
  };

  const handleFieldFilterChange = async (identifier: string, values: string[]) => {
    const previousSelections = fieldSelections;
    const nextSelections = {
      ...fieldSelections,
      [identifier]: values,
    };
    onFieldSelectionsChange(nextSelections);
    setSavingFilters(true);

    try {
      const data = await api.patch<any>(`/events/${eventSlug}/lucky_draw/eligibility_rules`, {
        fieldSelections: nextSelections,
      });

      applyEligibilityResponse(data);
      showSettingsSaved();
    } catch (error) {
      console.error("Error updating field filters:", error);
      message.error("Failed to update field filters");
      onFieldSelectionsChange(previousSelections);
    } finally {
      setSavingFilters(false);
    }
  };

  const handleClearFieldFilters = async () => {
    const previousSelections = fieldSelections;
    onFieldSelectionsChange({});
    setSavingFilters(true);

    try {
      const data = await api.patch<any>(`/events/${eventSlug}/lucky_draw/eligibility_rules`, {
        fieldSelections: {},
      });

      applyEligibilityResponse(data);
      showSettingsSaved();
    } catch (error) {
      console.error("Error clearing field filters:", error);
      message.error("Failed to clear field filters");
      onFieldSelectionsChange(previousSelections);
    } finally {
      setSavingFilters(false);
    }
  };

  const handleAddCategory = async () => {
    setAddingCategory(true);
    try {
      const data = await api.post<any>(`/events/${eventSlug}/lucky_draw/categories`, {
        category: {
          name: `Category ${categories.length + 1}`,
          prizeName: "Prize",
          winnerCount: 1,
        },
      });

      if (data.success && data.category) {
        onCategoriesChange([...categories, data.category]);
        message.success("Category added");
      }
    } catch (error) {
      console.error("Error adding category:", error);
      message.error("Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };

  const persistCategoryField = async (
    categoryId: string,
    payload: Partial<Pick<Category, "name" | "prizeName" | "winnerCount">>
  ) => {
    setCategoryBusyId(categoryId);
    try {
      const data = await api.patch<any>(`/events/${eventSlug}/lucky_draw/categories/${categoryId}`, {
        category: payload,
      });
      if (data?.category) {
        onCategoriesChange((prev) =>
          prev.map((cat) => (cat.id === data.category.id ? data.category : cat))
        );
      }
      showSettingsSaved();
    } catch (error) {
      console.error("Error updating category:", error);
      message.error("Failed to save category");
    } finally {
      setCategoryBusyId(null);
    }
  };

  const handleCategoryChange = (
    categoryId: string,
    field: keyof Category,
    value: string | number,
    persist = false
  ) => {
    const updated = categories.map((cat) =>
      cat.id === categoryId ? { ...cat, [field]: value } : cat
    );
    onCategoriesChange(updated);

    const payload: Record<string, string | number> = {};
    if (field === "name") payload.name = value as string;
    if (field === "prizeName") payload.prizeName = value as string;
    if (field === "winnerCount") payload.winnerCount = value as number;

    if (persist) {
      void persistCategoryField(categoryId, payload as any);
    }
  };

  const handleDeleteCategory = (categoryId: string) => {
    const categoryName = categories.find((c) => c.id === categoryId)?.name;
    Modal.confirm({
      title: "Delete this category?",
      content:
        "Deleting a category will also remove all winners under it. This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        setCategoryBusyId(categoryId);
        try {
          await api.delete(`/events/${eventSlug}/lucky_draw/categories/${categoryId}`);
          onCategoriesChange(categories.filter((cat) => cat.id !== categoryId));
          onWinnersChange(
            winners.filter(
              (winner) =>
                winner.categoryId !== categoryId &&
                (!categoryName || winner.category !== categoryName)
            )
          );
          message.success("Category deleted");
        } catch (error) {
          console.error("Error deleting category:", error);
          message.error("Failed to delete category");
        } finally {
          setCategoryBusyId(null);
        }
      },
    });
  };

  const handleAddAttendee = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setAddAttendeeModalOpen(true);
  };

  const handleAddAttendeeConfirm = async (attendee: Attendee) => {
    if (!selectedCategoryId) return;

    setCategoryBusyId(selectedCategoryId);
    try {
      const data = await api.post<any>(
        `/events/${eventSlug}/lucky_draw/categories/${selectedCategoryId}/participants`,
        { registrant_id: attendee.id }
      );

      if (data.success && data.category) {
        onCategoriesChange(
          categories.map((cat) => (cat.id === data.category.id ? data.category : cat))
        );

        if (data.participant) {
          const newWinner = formatWinnersFromCategory(data.category, [data.participant]);
          onWinnersChange(mergeWinners(winners, newWinner));
        }

        message.success(`${attendee.name} added as winner`);
      }
    } catch (error: any) {
      console.error("Error adding participant:", error);
      const apiMessage = error?.response?.data?.errors?.[0];
      message.error(apiMessage || "Failed to add participant");
    } finally {
      setCategoryBusyId(null);
      setAddAttendeeModalOpen(false);
      setSelectedCategoryId(null);
    }
  };

  const handleDeleteParticipant = async (categoryId: string, participantId: string) => {
    setCategoryBusyId(categoryId);
    try {
      await api.delete(
        `/events/${eventSlug}/lucky_draw/categories/${categoryId}/participants/${participantId}`
      );

      onCategoriesChange(
        categories.map((cat) =>
          cat.id === categoryId
            ? { ...cat, participants: cat.participants.filter((p) => p.id !== participantId) }
            : cat
        )
      );

      onWinnersChange(winners.filter((winner) => winner.id !== participantId));
      message.success("Winner removed");
    } catch (error) {
      console.error("Error removing participant:", error);
      message.error("Failed to remove participant");
    } finally {
      setCategoryBusyId(null);
    }
  };

  const handleDrawNow = async (categoryId: string) => {
    setDrawingCategoryId(categoryId);
    try {
      const data = await api.post<any>(
        `/events/${eventSlug}/lucky_draw/categories/${categoryId}/draw`
      );

      if (data.success && data.category) {
        onCategoriesChange(
          categories.map((cat) => (cat.id === data.category.id ? data.category : cat))
        );

        const winnerPayloads: Participant[] = data.winners || (data.winner ? [data.winner] : []);
        const newWinners = formatWinnersFromCategory(data.category, winnerPayloads);
        onWinnersChange(mergeWinners(winners, newWinners));

        if (data.winner) {
          message.success(`Winner: ${data.winner.name}`);
        }
      }
    } catch (error: any) {
      const apiMessage = error?.data.errors?.[0];
      message.error(apiMessage || "Failed to draw winner");
    } finally {
      setDrawingCategoryId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Lucky Draw Rules */}
        <div className="bg-white border border-[#EBEBEB] overflow-hidden flex flex-col col-span-1">
          {/* Header */}
          <div className="px-8 py-5 bg-white border-b border-[#EBEBEB] flex items-center gap-3">
            <ControlOutlined className="text-xl text-black/85" />
            <span className="text-xl font-normal leading-6 text-black/85">
              Lucky Draw Rules
            </span>
          </div>

          {/* Content */}
          <div className="p-8 flex flex-col gap-5">
            <p className="text-sm text-black/65 m-0">
              Only attendees who have attended the following (and match any selected field filters)
              are eligible for the lucky draw:
            </p>

            <div className="rounded border border-[#D6E4FF] bg-[#F0F5FF] p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#2F54EB]">
                Eligibility Summary
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-black/85">
                  {eligibleCount}
                </span>
                <span className="text-sm text-black/65">eligible registrants</span>
              </div>
              <div className="mt-2 text-xs text-black/60">
                {noFiltersApplied
                  ? "No filters selected — drawing from all registrants."
                  : "Filters applied — drawing only from eligible registrants."}
              </div>
              {allRulesUnchecked && hasFieldFilters && (
                <div className="mt-1 text-xs text-black/60">
                  No scan points selected — applying registration field filters only.
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {eligibilityRules.map((rule) => (
                <div key={rule.id}>
                  <Checkbox
                    checked={rule.checked}
                    disabled={savingRuleId === rule.id}
                    onChange={(e) => handleRuleChange(rule.id, e.target.checked)}
                  >
                    {rule.name}
                  </Checkbox>
                </div>
              ))}
            </div>

            {filterOptions.fields.length > 0 && (
              <>
                <div className="h-px bg-[#EBEBEB]" />

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-black/85">
                    Registration Field Filters
                  </span>
                  <button
                    type="button"
                    onClick={handleClearFieldFilters}
                    disabled={savingFilters}
                    className="text-sm font-semibold text-[#4277BB] hover:text-[#4277BB]/80 bg-transparent border-none cursor-pointer disabled:opacity-50"
                  >
                    Clear Filters
                  </button>
                </div>

                <p className="text-xs text-black/60 m-0">
                  Optional: only include registrants who selected these options.
                </p>

                <div className="flex flex-col gap-4">
                  {filterOptions.fields.map((field, idx) => (
                    <div key={field.fieldId} className="flex flex-col gap-2">
                      <div className="text-sm text-black/85">{field.label}</div>
                      <CheckboxGroup
                        options={field.options.map((opt) => ({
                          label: opt.label,
                          value: opt.value,
                        }))}
                        value={fieldSelections[field.identifier] || []}
                        onChange={(checked) =>
                          handleFieldFilterChange(field.identifier, checked as string[])
                        }
                        className="flex flex-col gap-2"
                        size="small"
                        disabled={savingFilters}
                      />
                      {idx < filterOptions.fields.length - 1 ? (
                        <div className="h-px bg-[#EBEBEB]" />
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Draw Winners */}
        <DrawWinnersCard
          categories={categories}
          busyCategoryId={categoryBusyId}
          addingCategory={addingCategory}
          drawingCategoryId={drawingCategoryId}
          onAddCategory={handleAddCategory}
          onDeleteCategory={handleDeleteCategory}
          onCategoryChange={handleCategoryChange}
          onDrawNow={handleDrawNow}
          onAddAttendee={handleAddAttendee}
          onDeleteParticipant={handleDeleteParticipant}
        />
      </div>

      {/* Add Attendee Modal */}
      <AddAttendeeModal
        open={addAttendeeModalOpen}
        onClose={() => {
          setAddAttendeeModalOpen(false);
          setSelectedCategoryId(null);
        }}
        onAdd={handleAddAttendeeConfirm}
        attendees={attendees}
        excludeIds={getExcludedAttendeeIds()}
      />
    </div>
  );
};

export default PreLuckyDrawSettings;
